import React, { useState } from 'react';
import { Dropdown, TextField, Button, unmaskValue } from '@cmsgov/ds-healthcare-gov';
import { runValidations, FieldValidation } from '../../../utilities/validation';
import { validators } from '../../../utilities/validators';
import { frequencyTypes, frequencyLabel, FrequencyType } from '../../../common/enums/FrequencyTypes';
import { expenseTypes, expenseLabel, ExpenseType } from '../../../common/enums/ExpenseTypes';
import { useTranslation } from 'react-i18next';

import type { Expense } from '../IncomeCalculator';

type Props = {
  amount?: string;
  expenseFrequency?: FrequencyType;
  expenseType?: ExpenseType;
  onCancelAddExpense: () => void;
  onDoneClick: (expenseRecord: Expense) => void;
};

const ExpenseFields = (props: Props) => {
  const [amount, setAmount] = useState(props.amount ?? '');
  const [expenseFrequency, setExpenseFrequency] = useState<FrequencyType | ''>(props.expenseFrequency ?? '');
  const [expenseType, setExpenseType] = useState<ExpenseType | ''>(props.expenseType ?? '');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const { t } = useTranslation();

  const handleUpdateField = (fieldName: string, value: string) => {
    switch (fieldName) {
      case 'expenseType':
        setExpenseType(value as ExpenseType);
        break;
      case 'expenseFrequency':
        setExpenseFrequency(value as FrequencyType);
        break;
      default:
        throw new Error('Not a valid field name');
    }
    if (formErrors[fieldName]) {
      validate(fieldName);
    }
  };

  const handleUpdateAmount = (event: React.ChangeEvent<HTMLInputElement>, blur: boolean = false) => {
    const amount = unmaskValue(event.target.value, 'currency');
    setAmount(amount);
    if (blur) {
      validate('amount');
    }
  };

  const getValidations = (): Array<FieldValidation> => {
    const { expenseTypeRequired, frequencyRequired, betweenNegMillionAndPosMillion } = validators;
    const validations: Array<FieldValidation> = [];
    validations.push(['expenseType', expenseTypeRequired, expenseType]);
    validations.push(['amount', betweenNegMillionAndPosMillion, amount]);
    validations.push(['expenseFrequency', frequencyRequired, expenseFrequency]);
    return validations;
  };

  const validate = async (fieldName?: string) => {
    const { errors } = await runValidations(getValidations(), formErrors, fieldName);
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDoneClick = async () => {
    if (await validate()) {
      // if all fields pass validation
      // calculate the total Expense based on the field state (so this logic only lives here, in one place)
      // then pass entire Expense state up to be stored in parent container
      const multipliers = {
        [frequencyTypes.YEARLY]: 1,
        [frequencyTypes.ONE_TIME_PAYMENT]: 1,
        [frequencyTypes.MONTHLY]: 12,
        [frequencyTypes.WEEKLY]: 51.96,
        '': 0,
      } as const;

      const calculatedTotal = parseFloat(amount) * multipliers[expenseFrequency];
      if (expenseType && expenseFrequency) {
        props.onDoneClick({ amount, expenseType, expenseFrequency, calculatedTotal });
      }
    }
  };

  const handleCancelClick = () => {
    props.onCancelAddExpense();
  };

  return (
    <div className="ds-l-col--12">
      <div className="ds-l-row">
        <div className="ds-l-md-col--4">
          <Dropdown
            name="expense-type"
            label={t('expenseType')}
            options={[
              { label: t(expenseLabel.DEFAULT), value: '' },
              { label: t(expenseLabel.STUDENT_LOAN_INTEREST), value: expenseTypes.STUDENT_LOAN_INTEREST },
              { label: t(expenseLabel.ALIMONY_PAYMENT), value: expenseTypes.ALIMONY_PAYMENT },
              { label: t(expenseLabel.IRA_CONTRIBUTIONS), value: expenseTypes.IRA_CONTRIBUTIONS },
              { label: t(expenseLabel.EDUCATOR_EXPENSES), value: expenseTypes.EDUCATOR_EXPENSES },
            ]}
            onChange={(e) => handleUpdateField('expenseType', e.currentTarget.value)}
            onBlur={() => validate('expenseType')}
            value={expenseType}
            errorMessage={formErrors.expenseType}
          />
        </div>
        <div className="ds-l-col--12 ds-l-md-col--auto">
          <TextField
            ariaLabel="Enter Expense amount in dollars."
            label={t('amount')}
            mask="currency"
            inputMode="numeric"
            pattern="\d*"
            type="text"
            name="amount"
            size="medium"
            onChange={(e) => handleUpdateAmount(e)}
            onBlur={(e) => handleUpdateAmount(e, true)} // blur will format the currency to two digits so we should update state
            value={amount}
            errorMessage={formErrors.amount}
          />
        </div>
        <div className="ds-l-col--12 ds-l-md-col--4">
          <Dropdown
            name="expense-frequency"
            className="ds-l-md-col--4 ds-u-padding--0"
            label={t('howOften')}
            size="medium"
            options={[
              { label: t(frequencyLabel.DEFAULT), value: '' },
              { label: t(frequencyLabel.WEEKLY), value: frequencyTypes.WEEKLY },
              { label: t(frequencyLabel.MONTHLY), value: frequencyTypes.MONTHLY },
              { label: t(frequencyLabel.YEARLY), value: frequencyTypes.YEARLY },
              { label: t(frequencyLabel.ONE_TIME_PAYMENT), value: frequencyTypes.ONE_TIME_PAYMENT },
            ]}
            onChange={(e) => handleUpdateField('expenseFrequency', e.currentTarget.value)}
            onBlur={() => validate('expenseFrequency')}
            value={expenseFrequency}
            errorMessage={formErrors.expenseFrequency}
          />
        </div>
      </div>
      <div className="ds-l-row ds-u-margin-top--3">
        <div className="ds-l-col--12">
          <Button
            variation="solid"
            className="ds-u-margin-right--2 ds-u-margin-bottom--2"
            onClick={handleDoneClick}
            aria-label={`${t('calculate')} ${t('expenses')}`}
          >
            {t('calculate')}
          </Button>
          <Button onClick={handleCancelClick} aria-label={`${t('cancel')} ${t('expenses')}`}>
            {t('cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExpenseFields;
